"use client";

import { useActionState, useEffect, useRef, useState } from "react";

import Link from "next/link";

import { Button } from "@/components/button/button.component";
import { FieldErrorMessage } from "@/components/formError/fieldErrorMessage.component";
import { Text } from "@/components/text/text.component";

import { saveCart, type SaveCartState } from "@/app/actions/saveCart";
import { EVENTS } from "@/constants/analytics.constants";
import { FORM_EVENTS } from "@/constants/formAnalytics.constants";
import { useFormSubmitAttemptTag } from "@/hooks/useFormSubmitAttemptTag";
import useTagging from "@/hooks/useTagging";

export const SaveYourCartForm = ({
  title,
  supportingText,
  disclaimerText,
  privacyPolicyUrl,
  successMessage,
  action,
  formMarketingName,
  emailLabelVariant = "body3",
  emailLabelClassName = "font-bold",
  requiredLabelVariant,
  submitLabelClassName,
}: SaveYourCartFormProps) => {
  const [state, formAction, isPending] = useActionState(
    action ?? saveCart,
    SAVE_CART_INITIAL_STATE,
  );
  const [email, setEmail] = useState(state.email);
  const inputRef = useRef<HTMLInputElement>(null);
  const errorId = "save-cart-email-error";
  const successId = "save-cart-email-success";
  const [, setTag] = useTagging();
  const prevStateRef = useRef(state);

  const formEventConfig =
    formMarketingName === "email_capture_save_system"
      ? FORM_EVENTS.SAVE_SYSTEM.formEvent
      : FORM_EVENTS.SAVE_CART.formEvent;

  useFormSubmitAttemptTag(isPending, formEventConfig);

  useEffect(() => {
    if (prevStateRef.current === state) return;
    prevStateRef.current = state;

    if (state.error) {
      inputRef.current?.focus();
      setTag({
        event: EVENTS.FORM_SUBMIT_FAILURE,
        formEvent: {
          ...formEventConfig,
          fieldName: "E_Mail_Address",
          errorCode: state.error,
        },
      });
    }
    if (state.success) {
      setEmail("");
      setTag({
        event: EVENTS.FORM_SUBMIT_COMPLETE,
        outcome: "success",
        formEvent: formEventConfig,
      });
    }
  }, [state, setTag, formEventConfig]);

  const hasValue = email.length > 0;

  return (
    <div
      data-testid="save-your-cart"
      className="flex w-auto flex-col gap-4 p-5">
      <div className="flex flex-col gap-2">
        <Text variant="h6" data-testid="save-your-cart-title">
          {title}
        </Text>
        {supportingText && (
          <Text variant="body4" data-testid="save-your-cart-supporting-text">
            {supportingText}
          </Text>
        )}
      </div>

      <form action={formAction} noValidate className="flex flex-col gap-3">
        <input
          type="hidden"
          name="successMessage"
          value={successMessage ?? "Your cart has been saved."}
        />

        <label
          htmlFor="save-cart-email-input"
          className={
            requiredLabelVariant ? "flex flex-wrap gap-x-1" : undefined
          }>
          <Text variant={emailLabelVariant} className={emailLabelClassName}>
            <span className="lg:hidden">Email</span>
            <span className="hidden lg:inline">Email address</span>
            {!requiredLabelVariant ? (
              <>
                {" "}
                <span className="font-normal">(required)</span>
              </>
            ) : null}
          </Text>
          {requiredLabelVariant ? (
            <Text variant={requiredLabelVariant}>(required)</Text>
          ) : null}
        </label>

        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <input
              ref={inputRef}
              id="save-cart-email-input"
              name="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              autoComplete="email"
              aria-describedby={
                state.error ? errorId : state.success ? successId : undefined
              }
              aria-invalid={state.error ? "true" : undefined}
              className="focus:border-secondary w-full rounded-full border-2 border-gray-300 px-4 py-3 text-sm transition-colors outline-none focus:ring-0"
              data-testid="save-cart-email-input"
            />
            {hasValue && (
              <button
                type="button"
                onClick={() => {
                  setEmail("");
                  inputRef.current?.focus();
                }}
                aria-label="Clear email"
                className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                data-testid="save-cart-clear-button">
                <span aria-hidden="true">&times;</span>
              </button>
            )}
          </div>

          <Button
            secondary
            border
            type="submit"
            loading={isPending}
            aria-busy={isPending}
            className="shrink-0 sm:hidden"
            data-testid="save-cart-submit-button">
            <Text variant="body4" className={submitLabelClassName}>
              Save
            </Text>
          </Button>

          {hasValue && (
            <Button
              secondary
              type="submit"
              loading={isPending}
              aria-busy={isPending}
              className="hidden shrink-0 sm:flex"
              data-testid="save-cart-submit-button">
              <Text variant="body4" className={submitLabelClassName}>
                Save
              </Text>
            </Button>
          )}
        </div>

        {state.error && (
          <FieldErrorMessage
            id={errorId}
            className="text-xs"
            testId="save-cart-error">
            {state.error}
          </FieldErrorMessage>
        )}
      </form>

      {disclaimerText && (
        <Text variant="legal" data-testid="save-cart-disclaimer">
          {renderDisclaimer(disclaimerText, privacyPolicyUrl ?? null)}
        </Text>
      )}

      {state.success && (
        <p
          id={successId}
          role="status"
          aria-live="polite"
          className="flex items-center gap-2 text-sm"
          data-testid="save-cart-success">
          <span aria-hidden="true">&#10003;</span>
          {state.message}
        </p>
      )}
    </div>
  );
};

function renderDisclaimer(text: string, privacyUrl: string | null) {
  const SPLIT = "privacy policy";
  const idx = text.toLowerCase().indexOf(SPLIT);
  if (!privacyUrl || idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <Link
        href={privacyUrl}
        className="underline"
        target="_blank"
        rel="noopener noreferrer">
        {text.slice(idx, idx + SPLIT.length)}
      </Link>
      {text.slice(idx + SPLIT.length)}
    </>
  );
}

const SAVE_CART_INITIAL_STATE: SaveCartState = {
  email: "",
  error: "",
  message: "",
  success: false,
};

export type SaveYourCartFormProps = {
  title: string;
  supportingText: string | null;
  disclaimerText: string | null;
  privacyPolicyUrl: string | null;
  successMessage: string | null;
  action?: typeof saveCart;
  formMarketingName?: string;
  emailLabelVariant?: "body2" | "body3";
  emailLabelClassName?: string;
  requiredLabelVariant?: "body4";
  submitLabelClassName?: string;
};
